/**
 * Payment vouchers: record (with an optional supplier credit), list, read, and VOID
 * (D49 phase 5, 2026-09-21).
 *
 * A NEW module beside lib/accountsPayableApi.ts rather than an edit of it (that file
 * is being changed in parallel by the reports work; the two are reconciled when
 * they merge). Everything here rides on lib/apiClient.ts.
 *
 * Server refusals arrive as an Error carrying `code` (machine-readable) and, when
 * the server sent a list (the bills a credit went to, the vouchers behind a bill),
 * `validationErrors`. The copy shown to a person is a function of the CODE and that
 * data, never of the server's prose: the messages here are the only place it is
 * written, so a reworded server message can never change what a screen says.
 */
import { get, postJson } from './apiClient';
import { PAYMENT_DATE_TOO_LATE_MESSAGE } from '../utils/paymentOnReceipt';
import type { AccountsPayablePaymentRecord, PaymentTotalsByCurrency } from './accountsPayableApi';

/** `applied` is a live payment; `void` was reversed and no longer counts as money paid. */
export type PaymentStatus = 'applied' | 'void';

/** The supplier credit a payment created, as the voucher read returns it. */
export interface PaymentCredit {
  _id: string;
  vendorId: string;
  vendorName: string;
  currency: string;
  amount: number;
  /** What is still unapplied. Below `amount` once the credit has paid down another bill. */
  remaining: number;
  status: 'open' | 'used' | 'reversed';
  applications?: Array<{
    accountsPayableId: string;
    invoiceNumber?: string;
    amount: number;
    appliedAt?: string;
  }>;
}

export interface PaymentVoucher extends Omit<AccountsPayablePaymentRecord, 'status'> {
  status?: PaymentStatus;
  /** Set once voided: when, who (the signed-in user's email) and why. */
  voidedAt?: string | null;
  voidedBy?: string;
  voidReason?: string;
  /** The supplier credit this payment created, if the person ticked "hold the excess as credit". */
  credit?: PaymentCredit | null;
  allocations?: Array<{
    accountsPayableId: string;
    invoiceNumber?: string;
    supplierName?: string;
    amount: number;
    note?: string;
    invoiceDate?: string | null;
    dueDate?: string | null;
    totalAmount?: number;
    outstandingBeforePayment?: number;
    currency?: string;
  }>;
}

export interface PaymentVoucherList {
  items: PaymentVoucher[];
  page: number;
  limit: number;
  total: number;
  pages: number;
  /** Money totals count LIVE payments only: a voided payment is listed but not summed. */
  summary: {
    paymentCount: number;
    totalAmount: number;
    allocatedAmount: number;
    unallocatedAmount: number;
    /** One entry per currency present. A screen that labels money reads THIS, never the sums above. */
    byCurrency?: PaymentTotalsByCurrency;
  };
}

export interface ListPaymentVouchersParams {
  page?: number;
  limit?: number;
  supplier?: string;
  q?: string;
  status?: PaymentStatus | 'all' | '';
  method?: string;
  /** Only payments with an excess no credit holds: the leftovers still to be dealt with. */
  unallocatedOnly?: boolean;
}

export async function listPaymentVouchers(params: ListPaymentVouchersParams = {}) {
  const qs = new URLSearchParams();
  qs.set('page', String(params.page ?? 1));
  qs.set('limit', String(params.limit ?? 25));
  if (params.supplier) qs.set('supplier', params.supplier);
  if (params.q) qs.set('q', params.q);
  if (params.status) qs.set('status', params.status);
  if (params.method) qs.set('method', params.method);
  if (params.unallocatedOnly) qs.set('unallocatedOnly', 'true');
  return get<{ success: boolean; data: PaymentVoucherList }>(`/accounts-payable/payments?${qs.toString()}`);
}

export async function getPaymentVoucher(id: string) {
  return get<{ success: boolean; data: PaymentVoucher }>(`/accounts-payable/payments/${id}`);
}

/**
 * The body of "record a payment". `holdRemainderAsCredit` and `vendorId` are the
 * phase 5 additions, and BOTH default to "as before": without the tick an excess is
 * recorded as unallocated and no credit exists. The server never holds a credit
 * unless `holdRemainderAsCredit` is exactly `true`.
 */
export interface CreatePaymentBody {
  supplierName?: string;
  reference: string;
  chequeNumber?: string;
  onlineReferenceNumber?: string;
  paymentDate?: string;
  amount: number;
  currency?: string;
  method?: string;
  note?: string;
  /**
   * May be EMPTY only for an advance: `holdRemainderAsCredit: true`, a `vendorId`
   * and a `supplierName` that is that vendor's name.
   */
  allocations: Array<{
    accountsPayableId: string;
    amount: number;
    note?: string;
  }>;
  /** Hold what is left after the allocations as a supplier credit. Send only when the person ticked it. */
  holdRemainderAsCredit?: boolean;
  /** The registered vendor, for an advance with no bills. */
  vendorId?: string;
}

/** What "record a payment" returns: the voucher, plus the credit it opened when one was held. */
export type CreatePaymentResult = Omit<PaymentVoucher, 'credit'> & {
  credit?: {
    _id: string;
    vendorId: string;
    vendorName: string;
    currency: string;
    amount: number;
    /** How much of it went straight onto the vendor's unpaid bills, oldest due first. */
    applied: number;
    /** What is left, waiting for the vendor's next bill. */
    remaining: number;
    status: 'open' | 'used' | 'reversed';
  };
};

export async function createPaymentVoucher(body: CreatePaymentBody) {
  return postJson<{ success: boolean; data: CreatePaymentResult }>('/accounts-payable/payments', body);
}

/** The reason's length limits, mirrored from the server so the dialog can say so before sending. */
export const VOID_REASON_MIN = 3;
export const VOID_REASON_MAX = 500;

/**
 * Void a payment: it stays on record as void, each bill it paid goes back to what
 * it owed, and an unspent credit it made is taken back. Needs the FINANCIALS edit
 * right. Who voided it is the signed-in user; the body carries only the reason.
 */
export async function voidPaymentVoucher(id: string, reason: string) {
  return postJson<{ success: boolean; data: PaymentVoucher & { creditTakenBack?: { amount: number; currency: string } | null } }>(
    `/accounts-payable/payments/${id}/void`,
    { reason: reason.trim() }
  );
}

// ─── Copy, a function of the code ────────────────────────────────────────────────

/** A refusal from apiClient: `code` from the envelope, `validationErrors` from its `errors`. */
export interface ApiRefusal {
  code?: string;
  status?: number;
  validationErrors?: unknown[];
}

const asRefusal = (err: unknown): ApiRefusal => (err && typeof err === 'object' ? (err as ApiRefusal) : {});

/** The bills a payment's credit was applied to, read off a PAYMENT_CREDIT_ALREADY_USED refusal. */
export function creditUsedBills(err: unknown): string[] {
  const list = asRefusal(err).validationErrors;
  if (!Array.isArray(list)) return [];
  return list
    .map((row) => {
      const r = (row || {}) as { invoiceNumber?: string; accountsPayableId?: string };
      return String(r.invoiceNumber || r.accountsPayableId || '').trim();
    })
    .filter(Boolean);
}

/** The payment vouchers behind a bill, read off a BILL_HAS_PAYMENTS refusal (Mark Pending). */
export interface BillVoucherRef {
  id: string;
  reference: string;
  paymentDate?: string | null;
  amount: number;
  /** What this voucher put on THIS bill (a voucher may span several). */
  allocatedToBill?: number;
}

export function billVouchers(err: unknown): BillVoucherRef[] {
  const list = asRefusal(err).validationErrors;
  if (!Array.isArray(list)) return [];
  return list
    .map((row) => row as BillVoucherRef)
    .filter((row) => row && typeof row.id === 'string' && row.id !== '');
}

/**
 * What to tell the person when a VOID is refused. Plain language, by code. An
 * unrecognised code (or none) gets the generic line: the server's own sentence is
 * never shown.
 */
export function voidRefusalMessage(err: unknown): string {
  const code = asRefusal(err).code;
  switch (code) {
    case 'PAYMENT_CREDIT_ALREADY_USED': {
      const bills = creditUsedBills(err);
      return bills.length
        ? `This payment cannot be voided. It left a supplier credit, and that credit has already been used to pay ${bills.join(', ')}.`
        : 'This payment cannot be voided. It left a supplier credit, and that credit has already been used to pay other bills.';
    }
    case 'PAYMENT_ALREADY_VOID':
      return 'This payment has already been voided.';
    case 'PAYMENT_VOID_REASON_REQUIRED':
      return `Give a reason of ${VOID_REASON_MIN} to ${VOID_REASON_MAX} characters.`;
    case 'PAYMENT_VOID_REFERENCE_NOT_FOUND':
    case 'PAYMENT_VOID_REFERENCE_AMBIGUOUS':
      return "This payment is an older one and its entry on the bill cannot be told apart with certainty, so it was not voided. Nothing has changed. Ask for it to be corrected.";
    case 'PAYMENT_VOID_BILL_MISSING':
      return 'A bill this payment was applied to no longer exists, so it was not voided. Nothing has changed.';
    case 'PAYABLE_CHANGED':
    case 'PAYMENT_WRITE_CONFLICT':
      return 'A bill this payment paid was changed at the same moment. Nothing was voided. Try again.';
    case 'PAYMENT_NOT_FOUND':
      return 'This payment could not be found.';
    default:
      return 'The payment could not be voided. Nothing has changed. Try again.';
  }
}

/**
 * What to tell the person when a bill's Mark Pending / Mark Paid is refused, or
 * when a payment cannot record a credit (the Record payment window uses the second
 * half). By code; never the server's sentence.
 */
export function billActionRefusalMessage(err: unknown): string {
  const { code, status } = asRefusal(err);
  // No right to record payments (a 403): there is nothing to try again.
  if (status === 403) return 'You do not have permission to record payments.';
  switch (code) {
    case 'PAYMENT_DATE_IN_FUTURE':
      return PAYMENT_DATE_TOO_LATE_MESSAGE;
    case 'PAYMENT_DATE_INVALID':
      return 'That payment date is not a real date. Pick it from the calendar again.';
    case 'PAYMENT_REFERENCE_REQUIRED':
      return 'Enter a voucher reference for this payment.';
    case 'PAYMENT_AMOUNT_NOT_POSITIVE':
      return 'The payment amount must be more than zero.';
    case 'PAYMENT_ALLOCATION_REQUIRED':
      return 'Tick at least one invoice to pay, or tick the box to record the payment as an advance.';
    case 'ALLOCATION_AMOUNT_NOT_POSITIVE':
      return 'Every ticked invoice needs an amount above zero. Untick the ones you are not paying.';
    case 'ALLOCATION_EXCEEDS_PAYMENT':
      return 'The amounts against the invoices add up to more than the payment amount. Raise the payment amount or lower an invoice amount.';
    case 'ALLOCATION_EXCEEDS_OUTSTANDING':
      return 'An amount is more than that invoice still owes. Lower it to what is owed.';
    case 'ALLOCATION_SUPPLIER_MISMATCH':
      return 'These invoices are not all from the supplier named. Pay each supplier in its own payment.';
    case 'PAYABLE_NOT_FOUND':
      return 'An invoice in this payment could not be found. Close the window, reopen it and try again.';
    case 'BILL_HAS_PAYMENTS':
      return 'This bill has payments recorded against it, so it cannot be marked pending. Void those payments first.';
    case 'PAID_REQUIRES_PAYMENT':
      return 'A bill is paid by recording a payment against it. Use Record a payment.';
    case 'PAYMENT_CREDIT_VENDOR_NOT_MATCHED':
      return 'The excess cannot be held as a credit: this supplier is not matched to a registered vendor. Untick it to record the excess as unallocated, or match the vendor first.';
    case 'ALLOCATION_CURRENCY_MISMATCH':
      return "A bill is in a different currency from this payment. Record the payment in the bill's currency, or pay these bills separately.";
    case 'PAYMENT_ALREADY_RECORDED':
      return 'A payment with this reference, amount and currency is already recorded for this supplier.';
    case 'PAYMENT_WRITE_CONFLICT':
    case 'PAYABLE_CHANGED':
      return 'A bill changed at the same moment. Nothing was saved. Try again.';
    default:
      return 'That could not be saved. Nothing has changed. Try again.';
  }
}
